import { convertAggregatedTicketSearchParamsToBackend, } from '../../aggregatedTicket';
import { convertOrderTicketToFrontend, groupOrderTicketsByTicketName, } from '../../orderTicket';
import { mapPackageTicketToFrontend, mapProductToFrontend, } from '../../product';
import { convertIsoDateFromDate, buildSearchParams, urlBuilder, } from '@starjack/utils';
import { createEntityApi } from '@starjack/api';
export const liftOperatorUrls = {
    liftOperator: urlBuilder('liftoperators', `liftoperators`),
    aggregatedTicket: urlBuilder('order', 'liftoperators/tickets-aggregated'),
    liftOperatorStatistics: urlBuilder('order', 'liftoperators/statistics'),
};
/**
 * LiftOperator endpoint definitions
 */
export const liftOperatorEndpoints = (builder) => ({
    getLiftOperators: builder.query({
        query: () => `${liftOperatorUrls.liftOperator}?size=1000`,
        transformResponse: (response) => {
            return response.content;
        },
        providesTags: ['liftOperator'],
    }),
    getLiftOperator: builder.query({
        query: ({ liftOperatorId }) => `${liftOperatorUrls.liftOperator}/${liftOperatorId}`,
        providesTags: ['liftOperator'],
    }),
    putLiftOperator: builder.mutation({
        query: (liftOperator) => ({
            method: 'PUT',
            url: `${liftOperatorUrls.liftOperator}/${liftOperator.id}`,
            body: liftOperator,
        }),
        invalidatesTags: ['liftOperator'],
    }),
    getAggregatedTickets: builder.query({
        query: (params = {}) => `${liftOperatorUrls.aggregatedTicket}${buildSearchParams(convertAggregatedTicketSearchParamsToBackend(params))}`,
        transformResponse: (response) => {
            return response.aggregatedTickets;
        },
        providesTags: ['liftOperator'],
    }),
    getStatistics: builder.query({
        query: (params = {}) => `${liftOperatorUrls.liftOperatorStatistics}${buildSearchParams(params)}`,
        providesTags: ['liftOperator'],
    }),
    getProductDetails: builder.query({
        query: ({ liftOperatorId, startDay }) => {
            const searchParams = new URLSearchParams();
            if (startDay) {
                searchParams.append('startDay', convertIsoDateFromDate(startDay));
            }
            searchParams.append('environment', 'B2B');
            return `${liftOperatorUrls.liftOperator}/${liftOperatorId}/products-details?${searchParams.toString()}`;
        },
        transformResponse: (requestResponse) => {
            const orderTickets = requestResponse.products.map(convertOrderTicketToFrontend);
            return groupOrderTicketsByTicketName(orderTickets);
        },
        providesTags: ['liftOperator'],
    }),
    getLiftOperatorProducts: builder.query({
        query: ({ liftOperatorId }) => urlBuilder('liftoperators', `liftoperators/${liftOperatorId}/products`),
        transformResponse: (response) => {
            const tickets = response.tickets.map(mapProductToFrontend);
            const packages = (response.packageTickets ?? []).map(mapPackageTicketToFrontend);
            return [...tickets, ...packages];
        },
        providesTags: ['liftOperator'],
    }),
});
export const liftOperatorApi = createEntityApi({
    tagTypes: ['liftOperator'],
    endpoints: liftOperatorEndpoints,
});
// Export hooks with explicit types for TypeScript inference
export const useGetLiftOperatorsQuery = liftOperatorApi.useGetLiftOperatorsQuery;
// Skips by default while no lift operator is resolvable (logged-out session,
// B2B user) — a bare id would otherwise request `/liftoperators/undefined`.
export const useGetLiftOperatorQuery = (params, options) => liftOperatorApi.useGetLiftOperatorQuery(params, {
    ...options,
    skip: options?.skip || !params?.liftOperatorId,
});
export const useGetLiftOperatorProductsQuery = liftOperatorApi.useGetLiftOperatorProductsQuery;
export const useLazyGetAggregatedTicketsQuery = liftOperatorApi.useLazyGetAggregatedTicketsQuery;
export const useLazyGetProductDetailsQuery = liftOperatorApi.useLazyGetProductDetailsQuery;
export const useGetStatisticsQuery = liftOperatorApi.useGetStatisticsQuery;
export const usePutLiftOperatorMutation = liftOperatorApi.usePutLiftOperatorMutation;
